import {axiTasksV2, GetListOutput, GetListParams} from '@shared/api';
import {currencyValueDivider} from '@shared/constants';
import {SupportedLanguage, TranslationDict} from '@shared/types';
import {validateData} from '@shared/utils';
import {
  VoucherConfig,
  VoucherConfigDetails,
  VoucherCurrencyAmount,
} from '../types';
import {
  v8nSchemaOfGetVoucherConfigsResponse,
  voucherConfigDetailsV8nSchema,
} from './backendSchemas';
import {
  GetVoucherConfigsResponse,
  VoucherConfigDetailsOnBackend,
} from './backendTypes';
import {
  mapVoucherConfigDetailsFromBackend,
  mapVoucherConfigFromBackend,
} from './dataMappers';

export interface CreateVoucherConfigParams {
  nameTrans: TranslationDict;
  amounts: VoucherCurrencyAmount[];
  ttlDays: number;
}

export interface UpdateVoucherConfigParams extends CreateVoucherConfigParams {
  id: string;
}

const mapVoucherConfigToBackend = ({
  nameTrans,
  amounts,
  ttlDays,
}: CreateVoucherConfigParams) => ({
  name: Object.fromEntries(
    [
      SupportedLanguage.English,
      SupportedLanguage.Portugal,
      SupportedLanguage.Tagalog,
      SupportedLanguage.Spanish,
    ]
      .filter(lang => nameTrans[lang]?.trim())
      .map(lang => [lang, nameTrans[lang].trim()]),
  ),
  amounts: amounts.map(({currency, amount}) => ({
    currency,
    amount: Math.round(amount * currencyValueDivider),
  })),
  voucher_ttl_days: ttlDays,
});

export const getVoucherConfigs = async ({
  page,
  pageSize,
}: GetListParams): Promise<GetListOutput<VoucherConfig>> => {
  const {data} = await axiTasksV2.get<GetVoucherConfigsResponse>(
    '/admin/rewards',
    {
      params: {reward_kind: 'voucher', page, per_page: pageSize},
    },
  );
  validateData(data, v8nSchemaOfGetVoucherConfigsResponse, 'getVoucherConfigs');
  return {
    items: data.array.map(config => mapVoucherConfigFromBackend(config)),
    page: data.page,
    pageSize: data.per_page,
    total: data.total,
    totalPages: data.total_pages,
  };
};

export const getVoucherConfig = async (
  id: string,
): Promise<VoucherConfigDetails> => {
  const {data} = await axiTasksV2.get<VoucherConfigDetailsOnBackend>(
    `/admin/rewards/${id}`,
  );
  validateData(data, voucherConfigDetailsV8nSchema, 'getVoucherConfig');
  return mapVoucherConfigDetailsFromBackend(data);
};

export const createVoucherConfig = async (
  params: CreateVoucherConfigParams,
): Promise<void> => {
  await axiTasksV2.post('/admin/rewards', {
    reward_kind: 'voucher',
    ...mapVoucherConfigToBackend(params),
  });
};

export const updateVoucherConfig = async ({
  id,
  ...params
}: UpdateVoucherConfigParams): Promise<void> => {
  await axiTasksV2.patch(
    `/admin/rewards/${id}`,
    mapVoucherConfigToBackend(params),
  );
};
